import { useEffect, useRef, useState } from "react";
import { Clock, Lock } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GRACES = { football: 0 };
const openPay = () => window.dispatchEvent(new CustomEvent("open-subscribe", { detail: { reason: "preview" } }));
const toState = (rem, GRACE) => rem > GRACE ? { phase: "free", freeLeft: rem - GRACE, graceLeft: GRACE }
  : rem > 0 ? { phase: "grace", freeLeft: 0, graceLeft: rem } : { phase: "expired", freeLeft: 0, graceLeft: 0 };

// 45s free preview → payment screen + 2-minute grace → stop. Clock is server-side (per day, per stream).
export function usePreviewGate(stream, active, onExpire) {
  const { isSubscribed } = useAuth();
  const [st, setSt] = useState(null);
  const base = useRef(null);
  const prev = useRef(null);
  const GRACE = GRACES[stream] ?? 120;
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  useEffect(() => {
    if (isSubscribed) { base.current = null; setSt({ phase: "subscribed" }); return; }
    if (!active) return;
    let alive = true;
    const sync = () => endpoints.previewStart(stream).then(({ data }) => {
      if (!alive) return;
      const rem = data.phase === "free" ? data.freeLeft + GRACE : data.phase === "grace" ? data.graceLeft : 0;
      base.current = { rem, at: Date.now() };
      setSt(toState(rem, GRACE));
    }).catch(() => {});
    sync();
    const tick = setInterval(() => {
      if (base.current) setSt(toState(Math.max(0, Math.round(base.current.rem - (Date.now() - base.current.at) / 1000)), GRACE));
    }, 1000);
    const re = setInterval(sync, 15000);
    return () => { alive = false; clearInterval(tick); clearInterval(re); };
  }, [stream, active, isSubscribed, GRACE]);

  useEffect(() => {
    if (st) window.dispatchEvent(new CustomEvent("preview-state", { detail: { stream, ...st } }));
    const ph = st?.phase;
    if (ph && ph !== prev.current) {
      if (ph === "grace") openPay();
      if (ph === "expired") { expireRef.current?.(); openPay(); }
      prev.current = ph;
    }
  }, [st, stream]);

  return st;
}

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const PreviewPill = ({ st, label = "Stream", testid = "preview-pill", className = "", compact = false }) => {
  if (!st || (st.phase !== "free" && st.phase !== "grace")) return null;
  const grace = st.phase === "grace";
  return (
    <button onClick={openPay} data-testid={testid}
      className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap border backdrop-blur-md transition-colors ${grace ? "bg-amber-500/15 border-amber-400/50 text-amber-200 hover:bg-amber-500/25" : "bg-black/60 border-white/15 text-white hover:bg-black/75"} ${className}`}>
      {grace ? <Lock className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5 text-[#E11D2A]" />}
      <span data-testid={`${testid}-text`}>{compact ? mmss(grace ? st.graceLeft : st.freeLeft) : grace ? `Complete payment · ${label} stops in ${mmss(st.graceLeft)}` : `Free preview · ${mmss(st.freeLeft)} left`}</span>
      {!compact && <span className="px-2 py-0.5 rounded-full bg-[#E11D2A] text-white text-[10px] uppercase">Subscribe</span>}
    </button>
  );
};
